import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { axiosInstance } from "../App";
import {
  Activity,
  Filter,
  Undo,
  User,
  Building2,
  FolderKanban,
  CheckSquare,
  AlertCircle,
  Clock,
  ArrowLeft
} from "lucide-react";
import { Card, CardHeader, CardTitle, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Input } from "../components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";

function ActivityLogs() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [logs, setLogs] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLog, setSelectedLog] = useState(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);

  // Filters
  const [companyFilter, setCompanyFilter] = useState("all");
  const [entityTypeFilter, setEntityTypeFilter] = useState("all");
  const [actionFilter, setActionFilter] = useState("all");
  const [dateFromFilter, setDateFromFilter] = useState("");
  const [dateToFilter, setDateToFilter] = useState("");

  useEffect(() => {
    fetchCompanies();
    fetchActivityLogs();
  }, [companyFilter, entityTypeFilter, actionFilter, dateFromFilter, dateToFilter]);

  const fetchCompanies = async () => {
    try {
      const response = await axiosInstance.get("/supervisor/companies");
      setCompanies(response.data);
    } catch (error) {
      console.error("Failed to fetch companies:", error);
    }
  };

  const fetchActivityLogs = async () => {
    try {
      setLoading(true);
      
      const params = new URLSearchParams();
      if (companyFilter !== "all") params.append("company_id", companyFilter);
      if (entityTypeFilter !== "all") params.append("entity_type", entityTypeFilter);
      if (actionFilter !== "all") params.append("action", actionFilter);
      if (dateFromFilter) params.append("date_from", dateFromFilter);
      if (dateToFilter) params.append("date_to", dateToFilter);
      params.append("limit", "100");

      const response = await axiosInstance.get(
        `/supervisor/activity-logs?${params.toString()}`
      );
      
      setLogs(response.data);
    } catch (error) {
      console.error("Failed to fetch activity logs:", error);
      toast.error("Aktivite logları yüklenemedi");
    } finally {
      setLoading(false);
    }
  };

  const handleUndo = async (logId) => {
    if (!window.confirm("Bu işlemi geri almak istediğinizden emin misiniz?")) {
      return;
    }

    try {
      await axiosInstance.post(
        `/supervisor/activity-logs/${logId}/undo`
      );
      
      toast.success("İşlem başarıyla geri alındı");
      fetchActivityLogs();
    } catch (error) {
      console.error("Failed to undo action:", error);
      toast.error(error.response?.data?.detail || "İşlem geri alınamadı");
    }
  };

  const getEntityIcon = (entityType) => {
    switch (entityType) {
      case "user":
        return <User className="h-4 w-4" />;
      case "company":
        return <Building2 className="h-4 w-4" />;
      case "project":
        return <FolderKanban className="h-4 w-4" />;
      case "task":
        return <CheckSquare className="h-4 w-4" />;
      default:
        return <Activity className="h-4 w-4" />;
    }
  };

  const getActionBadge = (action) => {
    const config = {
      create: { label: "Oluşturma", color: "bg-green-100 text-green-800" },
      update: { label: "Güncelleme", color: "bg-blue-100 text-blue-800" },
      delete: { label: "Silme", color: "bg-red-100 text-red-800" }
    };
    const actionConfig = config[action] || { label: action, color: "bg-gray-100 text-gray-800" };
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded ${actionConfig.color}`}>
        {actionConfig.label}
      </span>
    );
  };

  const getEntityTypeBadge = (entityType) => {
    const config = {
      user: { label: "Kullanıcı", color: "bg-purple-100 text-purple-800" },
      company: { label: "Firma", color: "bg-orange-100 text-orange-800" },
      project: { label: "Proje", color: "bg-cyan-100 text-cyan-800" },
      task: { label: "Görev", color: "bg-indigo-100 text-indigo-800" }
    };
    const typeConfig = config[entityType] || { label: entityType, color: "bg-gray-100 text-gray-800" };
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded ${typeConfig.color}`}>
        {typeConfig.label}
      </span>
    );
  };

  const formatDate = (isoDate) => {
    const date = new Date(isoDate);
    return new Intl.DateTimeFormat('tr-TR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  const getCompanyName = (companyId) => {
    if (!companyId) return "Sistem";
    const company = companies.find(c => c.id === companyId);
    return company?.name || companyId;
  };

  const showDetails = (log) => {
    setSelectedLog(log);
    setDetailsDialogOpen(true);
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate("/supervisor")}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              Geri
            </Button>
          </div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Activity className="h-8 w-8" />
            Aktivite Logları
          </h1>
          <p className="text-gray-500 mt-1">Tüm sistem aktivitelerini görüntüleyin ve yönetin</p>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Filter className="h-5 w-5" />
            Filtreler
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
            <div>
              <label className="text-sm font-medium mb-2 block">Firma</label>
              <Select value={companyFilter} onValueChange={setCompanyFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="Tüm Firmalar" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tüm Firmalar</SelectItem>
                  {companies.map((company) => (
                    <SelectItem key={company.id} value={company.id}>
                      {company.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block">Varlık Tipi</label>
              <Select value={entityTypeFilter} onValueChange={setEntityTypeFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="Tümü" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tümü</SelectItem>
                  <SelectItem value="user">Kullanıcı</SelectItem>
                  <SelectItem value="company">Firma</SelectItem>
                  <SelectItem value="project">Proje</SelectItem>
                  <SelectItem value="task">Görev</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block">İşlem Tipi</label>
              <Select value={actionFilter} onValueChange={setActionFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="Tümü" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tümü</SelectItem>
                  <SelectItem value="create">Oluşturma</SelectItem>
                  <SelectItem value="update">Güncelleme</SelectItem>
                  <SelectItem value="delete">Silme</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block">Başlangıç</label>
              <Input
                type="date"
                value={dateFromFilter}
                onChange={(e) => setDateFromFilter(e.target.value)}
              />
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block">Bitiş</label>
              <Input
                type="date"
                value={dateToFilter}
                onChange={(e) => setDateToFilter(e.target.value)}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Activity Logs List */}
      <Card>
        <CardHeader>
          <CardTitle>
            Aktivite Geçmişi ({logs.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
              <p className="mt-4 text-gray-500">Yükleniyor...</p>
            </div>
          ) : logs.length === 0 ? (
            <div className="text-center py-12">
              <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">Aktivite bulunamadı</p>
            </div>
          ) : (
            <div className="space-y-2">
              {logs.map((log) => (
                <div
                  key={log.id}
                  className="border rounded-lg p-4 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-3 flex-1">
                      <div className="mt-1">{getEntityIcon(log.entity_type)}</div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-2">
                          {getActionBadge(log.action)}
                          {getEntityTypeBadge(log.entity_type)}
                          <span className="px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded">
                            {getCompanyName(log.company_id)}
                          </span>
                        </div>
                        <p className="text-sm">
                          <span className="font-semibold">{log.user_name}</span>
                          {" "}
                          {log.action === "create" && "oluşturdu: "}
                          {log.action === "update" && "güncelledi: "}
                          {log.action === "delete" && "sildi: "}
                          <span className="font-medium">{log.entity_name}</span>
                        </p>
                        <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                          <span className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {formatDate(log.created_at)}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => showDetails(log)}
                      >
                        Detay
                      </Button>
                      {log.can_undo && !log.undone && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-orange-600 hover:text-orange-700"
                          onClick={() => handleUndo(log.id)}
                        >
                          <Undo className="h-4 w-4 mr-1" />
                          Geri Al
                        </Button>
                      )}
                      {log.undone && (
                        <span className="px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded">
                          Geri Alındı
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Details Dialog */}
      <Dialog open={detailsDialogOpen} onOpenChange={setDetailsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Aktivite Detayları</DialogTitle>
            <DialogDescription>
              İşlem hakkında detaylı bilgi
            </DialogDescription>
          </DialogHeader>
          {selectedLog && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-500">Kullanıcı</label>
                  <p className="text-sm mt-1">{selectedLog.user_name}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Firma</label>
                  <p className="text-sm mt-1">{getCompanyName(selectedLog.company_id)}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">İşlem</label>
                  <p className="text-sm mt-1">{getActionBadge(selectedLog.action)}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Varlık</label>
                  <p className="text-sm mt-1">{getEntityTypeBadge(selectedLog.entity_type)}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Tarih</label>
                  <p className="text-sm mt-1">{formatDate(selectedLog.created_at)}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Geri Alınabilir</label>
                  <p className="text-sm mt-1">{selectedLog.can_undo ? "Evet" : "Hayır"}</p>
                </div>
              </div>

              {selectedLog.changes && Object.keys(selectedLog.changes).length > 0 && (
                <div>
                  <label className="text-sm font-medium text-gray-500 block mb-2">Değişiklikler</label>
                  <div className="bg-gray-50 rounded p-3 space-y-2">
                    {Object.entries(selectedLog.changes).map(([field, change]) => (
                      <div key={field} className="text-sm">
                        <span className="font-medium">{field}:</span>
                        <div className="ml-4 mt-1">
                          <p className="text-red-600">- {JSON.stringify(change.old)}</p>
                          <p className="text-green-600">+ {JSON.stringify(change.new)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {selectedLog.new_data && (
                <div>
                  <label className="text-sm font-medium text-gray-500 block mb-2">Yeni Veri</label>
                  <pre className="bg-gray-50 rounded p-3 text-xs overflow-x-auto">
                    {JSON.stringify(selectedLog.new_data, null, 2)}
                  </pre>
                </div>
              )}

              {selectedLog.undone && (
                <div className="bg-yellow-50 border border-yellow-200 rounded p-3">
                  <p className="text-sm text-yellow-800">
                    Bu işlem <strong>{formatDate(selectedLog.undone_at)}</strong> tarihinde geri alındı.
                  </p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default ActivityLogs;
